import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { SignInWithApple, SignInWithAppleOptions, SignInWithAppleResponse } from '@capacitor-community/apple-sign-in';
import { environment } from '../../environments/environment';
import { IAuth } from '../../../assets/types/dtoTypes';

declare const AppleID: any;

export interface AppleTokenRequest {
  identityToken: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  role: string | null;
  isRegistration: boolean;
}

@Injectable({ providedIn: 'root' })
export class SocialAuthService {
  private http = inject(HttpClient);

  private readonly googleLoginUrl = `${environment.apiUrl}/auth/google/login`;
  private readonly googleRegisterUrl = `${environment.apiUrl}/auth/google/register`;
  private readonly appleTokenUrl = `${environment.apiUrl}/auth/apple/token`;

  loginWithGoogle(): void {
    localStorage.removeItem('isGoogleRegistration');
    localStorage.removeItem('socialRegistrationProvider');
    localStorage.removeItem('role');
    window.location.href = this.googleLoginUrl;
  }

  registerWithGoogle(role: string): void {
    localStorage.setItem('isGoogleRegistration', 'true');
    localStorage.setItem('socialRegistrationProvider', 'google');
    localStorage.setItem('role', role);
    window.location.href = `${this.googleRegisterUrl}?role=${role}`;
  }

  loginWithApple(role: string | null = null, isRegistration = false): Observable<IAuth> {
    if (Capacitor.isNativePlatform()) {
      return this.nativeAppleSignIn(role, isRegistration);
    }
    return this.webAppleSignIn(role, isRegistration);
  }

  private nativeAppleSignIn(role: string | null, isRegistration: boolean): Observable<IAuth> {
    const options: SignInWithAppleOptions = {
      clientId: environment.appleNativeClientId,
      redirectURI: environment.appleRedirectUri,
      scopes: 'name email',
    };

    return from(
      SignInWithApple.authorize(options).then((result: SignInWithAppleResponse) => {
        const payload: AppleTokenRequest = {
          identityToken: result.response.identityToken,
          firstName: result.response.givenName ?? null,
          lastName: result.response.familyName ?? null,
          email: result.response.email ?? null,
          role,
          isRegistration,
        };
        return this.http.post<IAuth>(this.appleTokenUrl, payload).toPromise();
      })
    ) as Observable<IAuth>;
  }

  private webAppleSignIn(role: string | null, isRegistration: boolean): Observable<IAuth> {
    AppleID.auth.init({
      clientId: environment.appleClientId,
      scope: 'name email',
      redirectURI: environment.appleRedirectUri,
      usePopup: true,
    });

    return from(
      AppleID.auth.signIn().then((data: any) => {
        const payload: AppleTokenRequest = {
          identityToken: data.authorization.id_token,
          firstName: data.user?.name?.firstName ?? null,
          lastName: data.user?.name?.lastName ?? null,
          email: data.user?.email ?? null,
          role,
          isRegistration,
        };
        return this.http.post<IAuth>(this.appleTokenUrl, payload).toPromise();
      })
    ) as Observable<IAuth>;
  }
}
